// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CartLineModel } from "@insite/client-framework/Types/ApiModels";

// setting up partitioning process depending on the fulfillment method
export const groupCartLines = (
    cartLines: CartLineModel[],
    fulfillmentMethod: string,
    isCustomGrouping?: boolean,
    shipGroupingData?: any,
) => {
    const inStock: CartLineModel[] = [];
    const partially: CartLineModel[] = [];
    const onBackOrder: CartLineModel[] = [];
    const inStockCustom = [] as any;
    let shippingMapper = [] as any;

    if (isCustomGrouping && fulfillmentMethod.toLowerCase() === "ship" && shipGroupingData) {
        shippingMapper = shipGroupingData.reduce((res: any, value: any) => {
            res[value.ProductId] = { qty: value.QuantityAvailable, warehouse: value.PlantDescription };
            if (value.PlantDescription) {
                inStockCustom[value.PlantDescription] = {
                    warehouse: value.PlantDescription,
                    lines: [],
                };
            }
            return res;
        }, {});
    }

    // this code should be split into two functions later on
    // eslint-disable-next-line array-callback-return
    cartLines?.map(line => {
        if (fulfillmentMethod && fulfillmentMethod.toLocaleLowerCase() === "pickup") {
            const qty = parseInt(line.properties["totalInSelectedPlant"], 10);
            if (qty === 0 || !line.properties?.hasOwnProperty("totalInSelectedPlant") || !line.qtyOrdered) {
                onBackOrder.push(line);
            } else if (line.qtyOrdered > qty) {
                if (isCustomGrouping) {
                    // split the line into two line with different qty
                    const qtyLeftOnOrder = line?.qtyOrdered - qty;
                    const splitLineBackOrder = {
                        ...line,
                        properties: {
                            ...line.properties,
                            qtyOrdered: qtyLeftOnOrder.toString(),
                        },
                    };
                    const splitLinePartial = {
                        ...line,
                        properties: {
                            ...line.properties,
                            qtyOrdered: qty.toString(),
                        },
                    };
                    onBackOrder.push(splitLineBackOrder);
                    partially.push(splitLinePartial);
                } else {
                    partially.push(line);
                }
            } else {
                inStock.push(line);
            }
        }

        if (fulfillmentMethod && fulfillmentMethod.toLowerCase() === "ship") {
            if (!isCustomGrouping) {
                if (line.qtyOnHand === 0) {
                    onBackOrder.push(line);
                } else {
                    line.qtyOrdered && line.qtyOrdered > line.qtyOnHand ? partially.push(line) : inStock.push(line);
                }
            } else if (isCustomGrouping && !shipGroupingData) {
                // No OFA call or GCG site
                if (line.qtyOnHand === 0) {
                    onBackOrder.push(line);
                } else if (line.qtyOrdered && line.qtyOrdered > line.qtyOnHand) {
                    const qtyLeftOnOrder = line.qtyOrdered - line.qtyOnHand;
                    const splitLineBackOrder = {
                        ...line,
                        properties: {
                            ...line.properties,
                            qtyOrdered: qtyLeftOnOrder.toString(),
                        },
                    };
                    const splitLinePartial = {
                        ...line,
                        properties: {
                            ...line.properties,
                            qtyOrdered: line.qtyOnHand.toString(),
                        },
                    };
                    onBackOrder.push(splitLineBackOrder);
                    partially.push(splitLinePartial);
                } else {
                    inStock.push(line);
                }
            } else if (
                shippingMapper[line.erpNumber.replace(/^0+/, "")]?.qty === 0 ||
                line.qtyOrdered! > shippingMapper[line.erpNumber.replace(/^0+/, "")] === null
            ) {
                onBackOrder.push(line);
            } else if (line.qtyOrdered! > shippingMapper[line.erpNumber.replace(/^0+/, "")]?.qty) {
                const qtyLeftOnOrder = line.qtyOrdered! - shippingMapper[line.erpNumber.replace(/^0+/, "")]?.qty;
                const splitLineBackOrder = {
                    ...line,
                    properties: {
                        ...line.properties,
                        qtyOrdered: qtyLeftOnOrder.toString(),
                    },
                };
                const splitLineInStock = {
                    ...line,
                    properties: {
                        ...line.properties,
                        qtyOrdered: shippingMapper[line.erpNumber.replace(/^0+/, "")]?.qty.toString(),
                    },
                };
                onBackOrder.push(splitLineBackOrder);

                inStockCustom[shippingMapper[line.erpNumber.replace(/^0+/, "")]?.warehouse].lines.push({
                    line: splitLineInStock,
                    isSplitted: true,
                });
            } else {
                inStockCustom[shippingMapper[line.erpNumber.replace(/^0+/, "")]?.warehouse].lines.push({
                    line,
                    isSplitted: false,
                });
            }
        }
    });

    // order submit shipping as fulfillment objects
    if (isCustomGrouping && fulfillmentMethod && fulfillmentMethod.toLowerCase() === "ship") {
        return {
            inStockCustom,
            inStock,
            partially,
            onBackOrder,
        };
    }

    return {
        inStock,
        partially,
        onBackOrder,
    };
};
